import {
  Sort,
  useFundraisingSearchStore,
} from '@/store/fundraising-search.store';
import { generateSearchQuery } from './search-query.service';
import { apiServices } from '../api';
import { errorMessage } from '../utils/message.service';
import { FundraisingDetail } from '@/models/fundraising-detail.model';
import { FundraisingMeta } from '@/models/fundraising-meta.model';
import { getFundraisingAssets } from '@/modules/fundraising-assets/services/session/fundraising-assets.service';
import { FUNDRAISING_ASSETS_BASE_URL } from '@/modules/fundraising-assets/config/fundraising-assets.config';

export const searchResults = async (): Promise<boolean> => {
  setSearching(true);
  const query = generateSearchQuery();
  const results = await apiServices.fundraisingSearchService(query);
  if (results.isAxiosError) {
    setSearching(false);
    errorMessage('fundraisingSearchErrorMessage');
    return false;
  }
  setSearchResults(results.data.documents);
  setSearchMeta(results.meta);
  setSearching(false);
  return true;
};

export const setSearching = (loading: boolean): void => {
  const searchStore = useFundraisingSearchStore();
  searchStore.$patch({
    loading,
    loaded: loading ? false : true,
  });
};

export const getSearching = (): boolean => {
  const searchStore = useFundraisingSearchStore();
  return searchStore.getLoading;
};

export const setSearchMeta = async (meta: FundraisingMeta): Promise<void> => {
  const searchStore = useFundraisingSearchStore();
  searchStore.$patch({
    resultsCount: meta.found,
  });
};

export const setSearchResults = async (
  results: FundraisingDetail[]
): Promise<void> => {
  const searchStore = useFundraisingSearchStore();
  searchStore.$patch({
    results,
  });
};

export const getSearchResults = (): FundraisingDetail[] => {
  const searchStore = useFundraisingSearchStore();
  return searchStore.getResults;
};

export const setSearchKeyword = (keyword: string): void => {
  const searchStore = useFundraisingSearchStore();
  searchStore.$patch({
    keyword,
    page: 1,
    sort: undefined,
  });
};

export const getSearchKeyword = (): string => {
  const searchStore = useFundraisingSearchStore();
  return searchStore.getKeyword;
};

export const setSearchSort = (sort: Sort): void => {
  const searchStore = useFundraisingSearchStore();
  searchStore.$patch({
    sort,
  });
};

export const getSearchSort = (): Sort | undefined => {
  const searchStore = useFundraisingSearchStore();
  return searchStore.getSort;
};

export const setSearchPage = (page: number): void => {
  const searchStore = useFundraisingSearchStore();
  searchStore.$patch({
    page,
  });
};

export const getSearchPage = (): number => {
  const searchStore = useFundraisingSearchStore();
  return searchStore.getPage;
};

export const setSearchPageSize = (pagesize: number): void => {
  const searchStore = useFundraisingSearchStore();
  searchStore.$patch({
    pagesize,
  });
};

export const getSearchPageSize = (): number => {
  const searchStore = useFundraisingSearchStore();
  return searchStore.getPagesize;
};

export const getPagination = (): (string | number)[] => {
  const searchStore = useFundraisingSearchStore();
  const pagination = generatePageItems(
    searchStore.getPage,
    searchStore.getResultsCount,
    searchStore.getPagesize
  );
  return pagination;
};

export const getMaxPages = (): number => {
  const searchStore = useFundraisingSearchStore();
  return Math.ceil(
    searchStore.getResultsCount / searchStore.getPagesize
  );
};

export const generatePageItems = (
  current: number,
  total: number,
  pagesize: number
): (string | number)[] => {
  total = Math.ceil(total / pagesize);
  const center = [current - 2, current - 1, current, current + 1, current + 2],
    filteredCenter = center.filter((p) => p > 1 && p < total),
    includeThreeLeft = current === 5,
    includeThreeRight = current === total - 4,
    includeLeftDots = current > 5,
    includeRightDots = current < total - 4;

  if (includeThreeLeft) filteredCenter.unshift(2);
  if (includeThreeRight) filteredCenter.push(total - 1);

  if (includeLeftDots) filteredCenter.unshift(0);
  if (includeRightDots) filteredCenter.push(0);

  return [1, ...filteredCenter, total];
};

export const getResultProjectImage = (id: string): string => {
  const project0 = getFundraisingAssets('Project')[0];
  const defaultAsset = project0 ? project0.medium_landscape : '';
  const asset = getFundraisingAssets('Project').filter(
    (item) => item.uuid === id
  );
  return asset[0]
    ? `${FUNDRAISING_ASSETS_BASE_URL}${asset[0].medium_landscape}`
    : `${FUNDRAISING_ASSETS_BASE_URL}${defaultAsset}`;
};

export const getResultProjectAvatar = (id: string): string => {
  const avatar8 = getFundraisingAssets('Avatar')[8];
  const defaultAsset = avatar8 ? avatar8.full : '';
  const asset = getFundraisingAssets('Avatar').filter(
    (item) => item.uuid === id
  );
  return asset[0]
    ? `${FUNDRAISING_ASSETS_BASE_URL}${asset[0].full}`
    : `${FUNDRAISING_ASSETS_BASE_URL}${defaultAsset}`;
};
